import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import {
  aggregateBedsByDiet,
  collettivaDietLabel,
  matchCollettivaDiet,
  type DestinationWard,
  type SupplyRequestLine,
} from '@trackeat/shared';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { getSupabase } from '../../lib/supabase.js';
import { BedCard, DietPickerModal } from './bedForm.js';

// T-436 — Ordine per LETTO (wizard reparto). Equivalente digitale del foglio
// "Rilevazione Prenotazioni" (Antonio Alba): per ogni LETTO si sceglie la DIETA
// (menu a tendina dal modello distillato) + note (allergie/specifiche). La lista
// CRESCE per numero di letto e propone il letto successivo. In fondo la SINTESI
// aggregata (quante diete X, a quali letti) = scheda ordine unica per la cucina.
//
// Modello dati (bounded, nessuna tabella nuova): scrive lo STESSO supply_request
// del giorno del preposto. Ogni letto = una riga con `letto` valorizzato, qty=1,
// description=etichetta dieta, menu_label=codice dieta (round-trip), reparto=ward.
// Le righe bed-level (con `letto`) e quelle catalogo (senza) sono DISGIUNTE → il
// wizard preserva le righe catalogo e viceversa. L'aggregazione al piano somma
// per label (N letti stessa dieta = N pasti): downstream invariato.

type MealService = 'pranzo' | 'cena' | 'colazione';
const MEAL_SERVICES: ReadonlyArray<readonly [MealService, string]> = [
  ['pranzo', 'Pranzo'],
  ['cena', 'Cena'],
  ['colazione', 'Colazione'],
];
const todayIso = (): string => new Date().toISOString().slice(0, 10);

interface BedRow {
  key: string; // chiave locale stabile
  letto: string;
  dietCode: string;
  note: string;
  meal_service: MealService;
  reparto: string; // '' = destinazione senza reparti
}
interface ExistingOrder {
  id: string;
  status: string;
  lines: SupplyRequestLine[] | null;
  submitted_at: string | null;
  created_at: string;
}


let bedSeq = 0;
const newBedKey = (): string => `b${++bedSeq}`;

// Pranzo/cena/colazione sono ORDINI SEPARATI: una riga supply_requests per servizio.
const MEALS: MealService[] = ['colazione', 'pranzo', 'cena'];
// Servizio di una riga-ordine (invariante single-meal: tutte le sue righe hanno lo stesso).
const rowMealOf = (o: ExistingOrder): MealService => {
  for (const ln of o.lines ?? []) return (ln.meal_service ?? 'pranzo') as MealService;
  return 'pranzo';
};

export default function PrepostoBedsPage(): JSX.Element {
  const { profile, signOut } = useAuth();
  const sb = useMemo(() => getSupabase(), []);
  const preposto = profile?.preposto ?? null;

  const [loading, setLoading] = useState(true);
  const [dayLoading, setDayLoading] = useState(false);
  const [destName, setDestName] = useState('');
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [wards, setWards] = useState<DestinationWard[]>([]);
  const [selectedWard, setSelectedWard] = useState<string>('');
  const [serviceDate, setServiceDate] = useState<string>(todayIso());
  const [mealService, setMealService] = useState<MealService>('pranzo');
  const [beds, setBeds] = useState<BedRow[]>([]);
  // Righe NON bed-level (catalogo/libere) PER SERVIZIO → preservate al salvataggio del servizio.
  const [preservedByMeal, setPreservedByMeal] = useState<Record<MealService, SupplyRequestLine[]>>(() => ({ colazione: [], pranzo: [], cena: [] }));
  // T-436/T-443: l'ordine per letto ha DUE stati prima della cucina:
  //  - 'draft'     = IN CONTROLLO dal preposto (NON inviato al centro cottura);
  //  - 'submitted' = INVIATO al centro cottura (dopo la verifica del preposto).
  // La cucina vede solo 'submitted'. Pranzo/cena sono ordini SEPARATI → una riga
  // editabile PER SERVIZIO (mai fondere due servizi in una riga).
  const [editableByMeal, setEditableByMeal] = useState<Record<MealService, { id: string; submitted_at: string | null; status: 'draft' | 'submitted' } | null>>(() => ({ colazione: null, pranzo: null, cena: null }));
  const [legacyDupes, setLegacyDupes] = useState<string[]>([]);
  const [lockedServices, setLockedServices] = useState<ReadonlySet<MealService>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  // T-436: picker dieta (bed key aperto). La UI è il componente condiviso DietPickerModal.
  const [dietPickerFor, setDietPickerFor] = useState<string | null>(null);

  useEffect(() => {
    if (!preposto) return;
    let mounted = true;
    void (async () => {
      const { data } = await sb
        .from('destinations')
        .select('name,business_id,wards')
        .eq('id', preposto.destination_id)
        .maybeSingle();
      if (!mounted) return;
      const dd = (data as { name?: string; business_id?: string | null; wards?: DestinationWard[] | null } | null) ?? null;
      setDestName(dd?.name ?? '');
      setBusinessId(dd?.business_id ?? null);
      const wl = (dd?.wards ?? []).slice().sort((a, b) => a.sort_order - b.sort_order);
      setWards(wl);
      setSelectedWard(wl.length > 0 ? wl[0].name : '');
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [sb, preposto]);

  const loadDay = useCallback(async (): Promise<void> => {
    if (!preposto) return;
    setDayLoading(true);
    const { data, error } = await sb
      .from('supply_requests')
      .select('id,status,lines,submitted_at,created_at')
      .eq('destination_id', preposto.destination_id)
      .eq('requested_for_date', serviceDate)
      .is('archived_at', null)
      .in('status', ['draft', 'submitted', 'accepted', 'in_production', 'fulfilled'])
      .order('created_at', { ascending: false });
    if (error) {
      setDayLoading(false);
      setToast(`Errore caricamento ordine: ${error.message}`);
      return;
    }
    const orders = (data ?? []) as ExistingOrder[];
    const accepted = orders.filter((o) => o.status !== 'submitted' && o.status !== 'draft');
    // Ordine correggibile dal preposto = draft (in controllo) o submitted (inviato,
    // ma non ancora accettato). La cucina blocca i servizi accettati.
    const active = orders.filter((o) => o.status === 'draft' || o.status === 'submitted');
    const locked = new Set<MealService>();
    for (const o of accepted) {
      for (const ln of o.lines ?? []) locked.add((ln.meal_service ?? 'pranzo') as MealService);
    }
    // Righe attive RAGGRUPPATE per servizio (una riga = un servizio). Ogni servizio ha
    // la SUA riga editabile: modificare/inviare il pranzo non tocca la cena, e viceversa.
    const activeByMeal: Record<MealService, ExistingOrder[]> = { colazione: [], pranzo: [], cena: [] };
    for (const o of active) activeByMeal[rowMealOf(o)].push(o);

    const loadedBeds: BedRow[] = [];
    const preservedByMealNext: Record<MealService, SupplyRequestLine[]> = { colazione: [], pranzo: [], cena: [] };
    const editableByMealNext: Record<MealService, { id: string; submitted_at: string | null; status: 'draft' | 'submitted' } | null> = { colazione: null, pranzo: null, cena: null };
    const dupes: string[] = [];
    for (const m of MEALS) {
      const rowsForMeal = activeByMeal[m];
      const cur = rowsForMeal[0] ?? null;
      editableByMealNext[m] = cur ? { id: cur.id, submitted_at: cur.submitted_at, status: cur.status as 'draft' | 'submitted' } : null;
      const preserved: SupplyRequestLine[] = [];
      for (const ln of cur?.lines ?? []) {
        const letto = (ln.letto ?? '').trim();
        if (letto) {
          loadedBeds.push({
            key: newBedKey(),
            letto,
            dietCode: (ln.menu_label ?? '').trim() || matchCollettivaDiet(ln.description) || '',
            note: ln.notes ?? '',
            meal_service: m,
            reparto: ln.reparto ?? '',
          });
        } else {
          preserved.push(ln); // riga catalogo/libera → da preservare
        }
      }
      preservedByMealNext[m] = preserved;
      // Solo eventuali DUPLICATI dello STESSO servizio (mai le righe di un altro servizio).
      dupes.push(...rowsForMeal.slice(1).map((o) => o.id));
    }
    // Letti dei servizi GIÀ ACCETTATI dalla cucina: caricali in SOLA LETTURA così il
    // preposto li consulta anche qui (prima sparivano dopo l'accettazione). Non sono in
    // `editableByMeal` (il servizio è in `locked`) → mostrati non modificabili, non salvati.
    for (const o of accepted) {
      const m = rowMealOf(o);
      for (const ln of o.lines ?? []) {
        const letto = (ln.letto ?? '').trim();
        if (!letto) continue;
        loadedBeds.push({
          key: newBedKey(),
          letto,
          dietCode: (ln.menu_label ?? '').trim() || matchCollettivaDiet(ln.description) || '',
          note: ln.notes ?? '',
          meal_service: m,
          reparto: ln.reparto ?? '',
        });
      }
    }
    setBeds(loadedBeds);
    setPreservedByMeal(preservedByMealNext);
    setLockedServices(locked);
    setEditableByMeal(editableByMealNext);
    setLegacyDupes(dupes);
    setDayLoading(false);
  }, [sb, preposto, serviceDate]);

  useEffect(() => {
    if (loading) return;
    void loadDay();
  }, [loading, loadDay]);

  // Letti del contesto corrente (servizio + reparto selezionati).
  const ctxBeds = useMemo(
    () => beds.filter((b) => b.meal_service === mealService && b.reparto === selectedWard),
    [beds, mealService, selectedWard],
  );
  const currentLocked = lockedServices.has(mealService);
  // Riga ordine editabile del servizio CORRENTE (pranzo/cena = ordini separati).
  const editable = editableByMeal[mealService];

  // Prossimo numero di letto suggerito (max numerico nel contesto + 1).
  const nextLetto = useMemo(() => {
    let mx = 0;
    for (const b of ctxBeds) {
      const n = parseInt(b.letto, 10);
      if (Number.isFinite(n) && n > mx) mx = n;
    }
    return String(mx + 1);
  }, [ctxBeds]);

  const addBed = useCallback(() => {
    const key = newBedKey();
    setBeds((prev) => [
      ...prev,
      { key, letto: nextLetto, dietCode: '', note: '', meal_service: mealService, reparto: selectedWard },
    ]);
    // Apre subito il picker dieta per il nuovo letto (flusso: aggiungi → scegli).
    setDietPickerFor(key);
  }, [nextLetto, mealService, selectedWard]);

  const patchBed = useCallback((key: string, patch: Partial<BedRow>) => {
    setBeds((prev) => prev.map((b) => (b.key === key ? { ...b, ...patch } : b)));
  }, []);
  const removeBed = useCallback((key: string) => {
    setBeds((prev) => prev.filter((b) => b.key !== key));
  }, []);

  // Sintesi aggregata su TUTTI i letti del giorno (tutti i reparti/servizi), via
  // helper shared. Costruiamo righe fittizie coerenti con lo schema.
  // Sintesi/totale del SOLO servizio corrente: pranzo e cena sono ordini separati,
  // non vanno mai aggregati insieme.
  const aggregate = useMemo(() => {
    const asLines: SupplyRequestLine[] = beds
      .filter((b) => b.meal_service === mealService && b.letto.trim() && b.dietCode)
      .map((b) => ({
        meal_service: b.meal_service,
        menu_label: b.dietCode,
        recipe_id: null,
        description: collettivaDietLabel(b.dietCode),
        qty: 1,
        uom: 'pasti',
        reparto: b.reparto || null,
        letto: b.letto.trim(),
        notes: b.note || null,
      }));
    return aggregateBedsByDiet(asLines);
  }, [beds, mealService]);
  const totalBeds = useMemo(() => beds.filter((b) => b.meal_service === mealService && b.letto.trim() && b.dietCode).length, [beds, mealService]);

  // Righe da scrivere per il SOLO servizio corrente (la sua riga-ordine). Gli altri
  // servizi vivono in righe separate e non vengono toccati qui.
  const buildLines = useCallback((): SupplyRequestLine[] => {
    // Servizio accettato dalla cucina = sola lettura: non si scrive nulla (difensivo,
    // il footer di salvataggio è già nascosto quando currentLocked).
    if (lockedServices.has(mealService)) return [];
    const bedLines: SupplyRequestLine[] = [];
    for (const b of beds) {
      if (b.meal_service !== mealService) continue; // solo il servizio corrente
      const letto = b.letto.trim();
      if (!letto || !b.dietCode) continue;
      bedLines.push({
        meal_service: mealService,
        menu_label: b.dietCode,
        recipe_id: null,
        description: collettivaDietLabel(b.dietCode),
        qty: 1,
        uom: 'pasti',
        reparto: b.reparto || null,
        letto,
        notes: b.note.trim() || null,
      });
    }
    // Righe catalogo/libere del servizio corrente (preservate).
    return [...bedLines, ...preservedByMeal[mealService]];
  }, [beds, mealService, preservedByMeal, lockedServices]);

  // T-436 — salva l'ordine per letto. `target` è lo stato DESIDERATO:
  //  - 'draft'     → "Salva in controllo del preposto" (NON va al centro cottura);
  //  - 'submitted' → "Verifica e invia al centro cottura" (dopo il controllo).
  // L'ordine NON parte mai automaticamente per la cucina: ci arriva solo con
  // l'azione esplicita 'submitted', che è lo step di verifica del preposto.
  const saveOrder = useCallback(async (target: 'draft' | 'submitted') => {
    if (!preposto || dayLoading) return;
    if (!businessId) {
      setToast('La destinazione non ha un centro cottura associato.');
      return;
    }
    const incomplete = beds.filter((b) => b.letto.trim() && !b.dietCode);
    if (incomplete.length > 0) {
      setToast(`${incomplete.length} letto/i senza dieta: completa o rimuovi.`);
      return;
    }
    const lines = buildLines();
    if (lines.length === 0 && !editable) {
      setToast('Aggiungi almeno un letto con la sua dieta.');
      return;
    }
    setSubmitting(true);
    try {
      const nowIso = new Date().toISOString();
      const submittedAt = target === 'submitted' ? nowIso : null;
      if (editable && lines.length === 0) {
        // Tutto azzerato → ritira l'ordine (in controllo o inviato che sia).
        const { data: upd, error } = await sb
          .from('supply_requests')
          .update({ archived_at: nowIso })
          .eq('id', editable.id)
          .eq('status', editable.status)
          .select('id');
        if (error) throw error;
        if (!upd || upd.length === 0) {
          setToast('La cucina ha già preso in carico questo ordine: non è più modificabile.');
          await loadDay();
          return;
        }
        setToast('Ordine ritirato ✓');
      } else if (editable) {
        // Update: guard di concorrenza sullo stato ATTUALE (draft o submitted).
        // draft→submitted = invio; submitted→submitted = correzione dell'inviato.
        const { data: upd, error } = await sb
          .from('supply_requests')
          .update({ lines, status: target, submitted_at: submittedAt, submitted_by: target === 'submitted' ? (profile?.id ?? null) : null })
          .eq('id', editable.id)
          .eq('status', editable.status)
          .select('id');
        if (error) throw error;
        if (!upd || upd.length === 0) {
          setToast('La cucina ha già preso in carico questo ordine: non è più modificabile.');
          await loadDay();
          return;
        }
        setToast(target === 'submitted' ? 'Verificato e inviato al centro cottura ✓' : 'Salvato in controllo ✓ (non ancora inviato)');
      } else {
        const { error } = await sb.from('supply_requests').insert({
          tenant_id: preposto.tenant_id,
          business_id: businessId,
          destination_id: preposto.destination_id,
          requested_for_date: serviceDate,
          status: target,
          lines,
          attachment_paths: [],
          notes: null,
          submitted_at: submittedAt,
          submitted_by: target === 'submitted' ? (profile?.id ?? null) : null,
        });
        if (error) throw error;
        setToast(target === 'submitted' ? 'Verificato e inviato al centro cottura ✓' : 'Salvato in controllo ✓ (non ancora inviato)');
      }
      if (legacyDupes.length > 0) {
        await sb.from('supply_requests').update({ archived_at: nowIso }).in('id', legacyDupes).in('status', ['draft', 'submitted']);
      }
      // Preserva i letti DIGITATI ma non ancora salvati degli ALTRI servizi: il reload
      // ricarica solo ciò che è sul server, quindi ri-aggiungo il lavoro locale in corso.
      const keepOtherMeals = beds.filter((b) => b.meal_service !== mealService && b.letto.trim());
      await loadDay();
      if (keepOtherMeals.length > 0) {
        setBeds((prev) => {
          const present = new Set(prev.map((b) => `${b.meal_service}|${b.reparto}|${b.letto.trim()}`));
          const missing = keepOtherMeals.filter((b) => !present.has(`${b.meal_service}|${b.reparto}|${b.letto.trim()}`));
          return missing.length > 0 ? [...prev, ...missing] : prev;
        });
      }
    } catch (e) {
      setToast(`Errore: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSubmitting(false);
    }
  }, [sb, preposto, beds, mealService, buildLines, editable, legacyDupes, serviceDate, businessId, profile, dayLoading, loadDay]);

  if (!preposto) {
    return (
      <IonPage>
        <IonContent className="ion-padding">Accesso non valido.</IonContent>
      </IonPage>
    );
  }

  const submittedTime = editable?.submitted_at
    ? new Date(editable.submitted_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : null;
  const isSent = editable?.status === 'submitted';
  const isDraft = editable?.status === 'draft';

  return (
    <IonPage>
      <style>{`
        .bd-hero{background:linear-gradient(135deg,#1f4e5c,#2e7d8a);color:#fff;border-radius:16px;padding:16px 18px;margin:4px 0 14px;box-shadow:0 4px 14px rgba(0,0,0,.12)}
        .bd-hero h2{margin:0 0 2px;font-size:20px;font-weight:800}
        .bd-hero .sub{opacity:.85;font-size:13px}
        .bd-controls{display:flex;gap:10px;align-items:center;margin:0 2px 10px;flex-wrap:wrap}
        .bd-date input{border:1px solid var(--ion-color-step-300,#cfd8dc);border-radius:10px;padding:8px 10px;font-size:14px;background:var(--ion-background-color,#fff);color:inherit}
        .bd-wards{display:flex;flex-wrap:wrap;gap:6px;margin:0 2px 12px}
        .bd-chip{font-size:13px;padding:6px 12px;border-radius:999px;cursor:pointer;border:1px solid #cbd5e1;background:#fff;color:#475569;font-weight:500}
        .bd-chip.on{border-color:#1f4e5c;background:#1f4e5c;color:#fff;font-weight:700}
        .bd-help{background:#eef6f8;border:1px solid #cfe6ec;border-radius:12px;padding:10px 12px;font-size:13px;color:#245a68;line-height:1.4;margin:0 0 12px}
        .bd-status{border-radius:12px;padding:10px 14px;margin:0 0 12px;font-size:13px;line-height:1.35}
        .bd-status.draft{background:#eef4ff;border:1px solid #c3d6f5;color:#274a8a}
        .bd-status.sent{background:#fff7e6;border:1px solid #f0d9a8;color:#7a5b13}
        .bd-status.ok{background:#e8f5ec;border:1px solid #bfe3cb;color:#1e6b3a}
        .bd-card{background:var(--ion-card-background,#fff);border:1px solid #e5edef;border-radius:14px;padding:10px 12px;margin:0 0 10px;box-shadow:0 1px 6px rgba(0,0,0,.05)}
        .bd-card .top{display:flex;align-items:center;gap:10px;margin-bottom:8px}
        .bd-letto{width:70px}
        .bd-letto label,.bd-diet label,.bd-note label{display:block;font-size:11px;font-weight:700;color:#64748b;margin:0 0 3px 2px;text-transform:uppercase;letter-spacing:.03em}
        .bd-letto input{width:100%;box-sizing:border-box;border:1px solid #d3dde0;border-radius:9px;padding:9px 8px;font-size:16px;font-weight:700;text-align:center;background:var(--ion-background-color,#fff);color:inherit}
        .bd-diet{flex:1;min-width:120px}
        .bd-diet-btn{width:100%;box-sizing:border-box;display:flex;align-items:center;justify-content:space-between;gap:8px;border:1px solid #d3dde0;border-radius:9px;padding:10px 10px;font-size:14px;font-weight:600;background:var(--ion-background-color,#fff);color:inherit;cursor:pointer;text-align:left}
        .bd-diet-btn.empty{border-color:#e0a94f;background:#fffaf0;color:#9a6212;font-weight:600}
        .bd-diet-btn:disabled{opacity:.6}
        .bd-diet-btn .chev{color:#94a3b8;font-size:12px}
        .bd-pick-group{font-size:11px;font-weight:800;letter-spacing:.05em;text-transform:uppercase;color:#0b6b63;padding:14px 4px 4px}
        .bd-pick-item{display:flex;align-items:center;justify-content:space-between;width:100%;text-align:left;background:var(--ion-card-background,#fff);border:1px solid #e5edef;border-radius:11px;padding:13px 14px;margin:0 0 7px;font-size:15px;font-weight:600;color:#243b40;cursor:pointer}
        .bd-pick-item.on{border-color:#12a594;background:#e8f7f4;color:#0b6b63}
        .bd-pick-item .tick{color:#12a594;font-weight:800}
        .bd-note input{width:100%;box-sizing:border-box;border:1px solid #d3dde0;border-radius:9px;padding:8px;font-size:13px;background:var(--ion-background-color,#fff);color:inherit}
        .bd-del{background:none;border:none;color:#c0564f;font-size:18px;padding:4px 6px;cursor:pointer}
        .bd-empty{text-align:center;color:#64748b;padding:22px 12px;font-size:14px;line-height:1.5}
        .bd-add{width:100%;--border-radius:12px;margin-top:2px}
        .bd-sum{background:#f8fbfc;border:1px solid #dde9ec;border-radius:14px;padding:12px 14px;margin:14px 0 4px}
        .bd-sum h3{margin:0 0 8px;font-size:14px;font-weight:800;color:#1f4e5c}
        .bd-sum-row{display:flex;align-items:baseline;gap:8px;padding:6px 0;border-top:1px solid #eef2f3}
        .bd-sum-row:first-of-type{border-top:none}
        .bd-sum-diet{flex:1;font-size:14px;font-weight:700;color:#243b40}
        .bd-sum-n{font-size:15px;font-weight:800;color:#2e7d8a;min-width:28px;text-align:right}
        .bd-sum-beds{font-size:12px;color:#64748b;margin:2px 0 0}
      `}</style>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/preposto" />
          </IonButtons>
          <IonTitle>Ordine per letto</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => void signOut()}>Esci</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <IonSpinner />
          </div>
        ) : (
          <>
            <div className="bd-hero">
              <h2>{destName || 'La mia struttura'}</h2>
              <div className="sub">Un letto = una dieta · l'ordine resta in controllo dal preposto finché non lo invii</div>
            </div>

            <div className="bd-controls">
              <div className="bd-date">
                <input
                  type="date"
                  value={serviceDate}
                  onChange={(e) => setServiceDate(e.target.value || todayIso())}
                  aria-label="Data del servizio"
                />
              </div>
              <IonSegment value={mealService} onIonChange={(e) => setMealService((e.detail.value as MealService) ?? 'pranzo')}>
                {MEAL_SERVICES.map(([v, lab]) => (
                  <IonSegmentButton key={v} value={v}>
                    {lab}
                    {lockedServices.has(v) ? ' ✓' : ''}
                  </IonSegmentButton>
                ))}
              </IonSegment>
            </div>

            {wards.length > 0 && (
              <div className="bd-wards">
                {wards.map((w) => {
                  const has = beds.some((b) => b.reparto === w.name && b.letto.trim() && b.dietCode);
                  return (
                    <button
                      key={w.id}
                      type="button"
                      className={`bd-chip${selectedWard === w.name ? ' on' : ''}`}
                      onClick={() => setSelectedWard(w.name)}
                    >
                      {w.name}
                      {has ? ' •' : ''}
                    </button>
                  );
                })}
              </div>
            )}

            <button type="button" className="bd-help" style={{ width: '100%', textAlign: 'left', cursor: 'pointer' }} onClick={() => setShowHelp((s) => !s)}>
              <strong>ℹ️ Come si compila</strong> {showHelp ? '▲' : '▼'}
              {showHelp && (
                <div style={{ marginTop: 6 }}>
                  1. Aggiungi un letto (il numero si propone da solo).<br />
                  2. Scegli la <strong>dieta</strong> del paziente dal menu.<br />
                  3. Nelle <strong>note</strong> scrivi allergie o specifiche (es. «carne frullata», «senza lattosio»).<br />
                  4. La <strong>sintesi</strong> in fondo si aggiorna da sola: quante diete di ogni tipo e a quali letti.<br />
                  {wards.length > 0 && <>5. Cambia <strong>reparto</strong> in alto per inserire i letti di ciascun reparto.<br /></>}
                  6. Qui <strong>salvi in controllo</strong> (l'ordine resta da te). Per <strong>inviarlo</strong> al centro
                  cottura vai su <strong>«Riepilogo e invio»</strong> dalla home: lì vedi tutti i reparti insieme e scheduli.
                </div>
              )}
            </button>

            {isDraft && (
              <div className="bd-status draft">
                📝 <strong>Salvato in controllo</strong> — l'ordine è da te, <strong>non</strong> ancora inviato al centro
                cottura. Per inviarlo vai su <strong>«Riepilogo e invio»</strong> (dalla home del preposto): lì vedi tutti i
                reparti insieme, controlli e invii.
              </div>
            )}
            {isSent && (
              <div className="bd-status sent">
                📤 Inviato al centro cottura{submittedTime ? ` il ${submittedTime}` : ''} — puoi ancora correggerlo finché la cucina non lo accetta.
              </div>
            )}
            {currentLocked && (
              <div className="bd-status ok">✅ {MEAL_SERVICES.find(([v]) => v === mealService)?.[1]}: ordine accettato dalla cucina (sola lettura).</div>
            )}

            {dayLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                <IonSpinner />
              </div>
            ) : (
              <>
                {ctxBeds.length === 0 ? (
                  <div className="bd-empty">
                    Nessun letto inserito{wards.length > 0 && selectedWard ? ` per ${selectedWard}` : ''}.<br />
                    Tocca <strong>«+ Aggiungi letto»</strong> per iniziare.
                  </div>
                ) : (
                  ctxBeds.map((b) => (
                    <BedCard
                      key={b.key}
                      bed={{ key: b.key, letto: b.letto, dietCode: b.dietCode, note: b.note }}
                      readOnly={currentLocked}
                      active={dietPickerFor === b.key}
                      onPatch={(patch) => patchBed(b.key, patch)}
                      onRemove={() => removeBed(b.key)}
                      onOpenPicker={() => setDietPickerFor(b.key)}
                    />
                  ))
                )}

                {!currentLocked && (
                  <IonButton className="bd-add" fill="outline" onClick={addBed}>
                    + Aggiungi letto
                  </IonButton>
                )}

                {/* Sintesi aggregata (part B) — quante diete X, a quali letti */}
                {aggregate.length > 0 && (
                  <div className="bd-sum">
                    <h3>Sintesi ordine · {totalBeds} letti</h3>
                    {aggregate.map((d) => (
                      <div className="bd-sum-row" key={d.diet}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                            <span className="bd-sum-diet">{d.diet}</span>
                            <span className="bd-sum-n">{d.meals}</span>
                          </div>
                          <div className="bd-sum-beds">
                            {d.beds
                              .map((bd) => `${bd.reparto ? `${bd.reparto} ` : ''}letto ${bd.letto}${bd.notes ? ` (${bd.notes})` : ''}`)
                              .join(' · ')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            <div style={{ height: 12 }} />
          </>
        )}

        {/* T-441 — Picker dieta CONDIVISO (stesso di RepartoPublicPage) */}
        <DietPickerModal
          open={dietPickerFor !== null}
          currentCode={beds.find((b) => b.key === dietPickerFor)?.dietCode ?? ''}
          letto={beds.find((b) => b.key === dietPickerFor)?.letto}
          onSelect={(code) => {
            // Immessa la dieta: procedi automaticamente al letto SUCCESSIVO (senza
            // ri-cliccare "Aggiungi letto") → inserimento rapido e continuo.
            if (dietPickerFor) { patchBed(dietPickerFor, { dietCode: code }); addBed(); }
          }}
          onClose={() => {
            // Chiuso senza scegliere: il letto in eccesso (vuoto) viene rimosso.
            const cur = beds.find((b) => b.key === dietPickerFor);
            if (cur && !cur.dietCode) removeBed(cur.key);
            setDietPickerFor(null);
          }}
        />

        <IonToast isOpen={toast !== null} message={toast ?? ''} duration={2500} onDidDismiss={() => setToast(null)} />
      </IonContent>
      {!loading && !currentLocked && (
        <IonFooter>
          <IonToolbar>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '6px 12px' }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Totale: {totalBeds} letti</div>
              {isSent ? (
                // Già inviato al centro cottura: si possono solo fare correzioni
                // (o ritirare azzerando i letti) finché la cucina non accetta.
                <IonButton
                  expand="block"
                  disabled={submitting || dayLoading}
                  onClick={() => void saveOrder('submitted')}
                >
                  {submitting ? 'Salvataggio…' : totalBeds === 0 ? 'Ritira ordine' : 'Aggiorna ordine inviato'}
                </IonButton>
              ) : (
                // Questa pagina è l'INPUT per reparto: si SALVA in controllo, non
                // si invia. L'invio al centro cottura si fa dal «Riepilogo e invio»
                // (dove il preposto vede tutti i reparti insieme e schedula).
                <IonButton
                  expand="block"
                  disabled={submitting || dayLoading || (totalBeds === 0 && !editable)}
                  onClick={() => void saveOrder('draft')}
                >
                  {submitting ? 'Salvataggio…' : totalBeds === 0 && editable ? 'Ritira ordine' : 'Salva in controllo'}
                </IonButton>
              )}
            </div>
          </IonToolbar>
        </IonFooter>
      )}
    </IonPage>
  );
}
